import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useTablets } from '@/lib/api/tablet';
import { saveTabletGroup, useTabletGroups } from '@/lib/api/tabletGroup';
import { TabletGroup } from '@/lib/schema/tabletGroup';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const GroupFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  enabled: z.boolean(),
  memberIds: z.array(z.string()).min(1, 'Add at least one tablet'),
});
type GroupFormValues = z.infer<typeof GroupFormSchema>;

type GroupFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // null = create a new group
  group: TabletGroup | null;
};

export function GroupFormDialog({
  open,
  onOpenChange,
  group,
}: GroupFormDialogProps) {
  const [tablets] = useTablets();
  const [groups] = useTabletGroups();
  const [filter, setFilter] = useState('');

  const groupNameById = useMemo(
    () =>
      new Map<string, string>(
        (groups || []).filter((g) => g.id).map((g) => [g.id!, g.name])
      ),
    [groups]
  );

  // Tablets in a *different* group are shown but not selectable: a tablet
  // belongs to at most one group, so it must be removed there first.
  const rows = useMemo(() => {
    return (tablets || [])
      .filter((tablet) => tablet.id)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((tablet) => ({
        id: tablet.id!,
        name: tablet.name,
        location: tablet.location,
        otherGroupName:
          !!tablet.groupId && (!group?.id || tablet.groupId !== group.id)
            ? groupNameById.get(tablet.groupId) || 'another group'
            : null,
      }));
  }, [tablets, group, groupNameById]);

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        row.location.toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const form = useForm<GroupFormValues>({
    resolver: zodResolver(GroupFormSchema),
    defaultValues: { name: '', enabled: true, memberIds: [] },
  });

  useEffect(() => {
    if (open) {
      setFilter('');
      form.reset({
        name: group?.name || '',
        enabled: group?.enabled ?? true,
        memberIds: group?.id
          ? (tablets || [])
              .filter((t) => t.id && t.groupId === group.id)
              .map((t) => t.id!)
          : [],
      });
    }
    // Intentionally not re-resetting on tablet snapshot updates mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group]);

  const mutation = useMutation({
    mutationFn: (value: GroupFormValues) => {
      const originalIds = group?.id
        ? (tablets || [])
            .filter((t) => t.id && t.groupId === group.id)
            .map((t) => t.id!)
        : [];
      const addIds = value.memberIds.filter((id) => !originalIds.includes(id));
      const removeIds = originalIds.filter(
        (id) => !value.memberIds.includes(id)
      );
      return saveTabletGroup(
        { id: group?.id, name: value.name, enabled: value.enabled },
        addIds,
        removeIds
      );
    },
    onSuccess: () => {
      toast.success(group ? 'Group updated' : 'Group created');
      onOpenChange(false);
    },
    onError: (error) => {
      console.error('Error saving tablet group:', error);
      toast.error('Failed to save group');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{group ? 'Edit group' : 'New group'}</DialogTitle>
          <DialogDescription>
            Tablets in an enabled group report ON to the suite feed; all other
            tablets report OFF.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((value) => {
              if (
                groups?.some(
                  (g) =>
                    g.name.trim().toLowerCase() ===
                      value.name.trim().toLowerCase() && g.id !== group?.id
                )
              ) {
                form.setError('name', {
                  type: 'manual',
                  message: 'Name must be unique',
                });
                return;
              }
              mutation.mutate(value);
            })}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      inError={!!form.formState.errors.name}
                      placeholder="Enter group name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message || ''}</FormMessage>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="memberIds"
              render={({ field, fieldState }) => {
                const selected = new Set(field.value);
                const selectableFiltered = filteredRows.filter(
                  (row) => !row.otherGroupName
                );
                const allFilteredSelected =
                  selectableFiltered.length > 0 &&
                  selectableFiltered.every((row) => selected.has(row.id));

                const toggle = (id: string, checked: boolean) => {
                  field.onChange(
                    checked
                      ? [...field.value, id]
                      : field.value.filter((v) => v !== id)
                  );
                };
                const selectAllFiltered = () => {
                  const union = new Set(field.value);
                  selectableFiltered.forEach((row) => union.add(row.id));
                  field.onChange([...union]);
                };
                const clearFiltered = () => {
                  const filteredIds = new Set(
                    filteredRows.map((row) => row.id)
                  );
                  field.onChange(
                    field.value.filter((id) => !filteredIds.has(id))
                  );
                };

                return (
                  <FormItem>
                    <FormLabel>Tablets</FormLabel>
                    <div className="flex items-center gap-2">
                      <Input
                        autoComplete="off"
                        placeholder="Filter tablets... e.g. suite_m2"
                        value={filter}
                        onChange={(event) => setFilter(event.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={selectAllFiltered}
                        disabled={
                          selectableFiltered.length === 0 ||
                          allFilteredSelected
                        }
                      >
                        Select all ({selectableFiltered.length})
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={clearFiltered}
                        disabled={
                          !filteredRows.some((row) => selected.has(row.id))
                        }
                      >
                        Clear
                      </Button>
                    </div>
                    <FormControl>
                      <div className="rounded-md border max-h-64 overflow-y-auto">
                        {filteredRows.length ? (
                          filteredRows.map((row) => (
                            <label
                              key={row.id}
                              className={`flex items-center gap-3 px-3 py-1.5 border-b last:border-b-0 ${
                                row.otherGroupName
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'cursor-pointer hover:bg-muted/50'
                              }`}
                            >
                              <Checkbox
                                checked={selected.has(row.id)}
                                disabled={!!row.otherGroupName}
                                onCheckedChange={(checked) =>
                                  toggle(row.id, checked === true)
                                }
                              />
                              <span className="text-sm">{row.name}</span>
                              {row.location !== row.name && (
                                <span className="text-sm text-muted-foreground">
                                  {row.location}
                                </span>
                              )}
                              {row.otherGroupName && (
                                <span className="ml-auto text-xs text-muted-foreground">
                                  in {row.otherGroupName}
                                </span>
                              )}
                            </label>
                          ))
                        ) : (
                          <p className="p-4 text-center text-sm text-muted-foreground">
                            No tablets match "{filter}"
                          </p>
                        )}
                      </div>
                    </FormControl>
                    <div className="text-sm text-muted-foreground">
                      {field.value.length} tablet(s) selected
                    </div>
                    <FormMessage>{fieldState.error?.message || ''}</FormMessage>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <FormLabel>Enabled</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {group
                  ? mutation.isPending
                    ? 'Updating...'
                    : 'Update group'
                  : mutation.isPending
                    ? 'Creating...'
                    : 'Create group'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
